import { useEffect } from 'react';
import { AppSidebar } from "@/components/app-sidebar";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import BigCalendarComponent from "@/components/calendar/BigCalendarComponent";
import { useOrganizationPolicies } from "@/hooks/useDNSPolicies";
import { useCalendarEvents } from "@/hooks/useCalendarEvents";
import { useUserOrganization } from "@/hooks/useOrganization";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Clock, Ban, BookOpen } from "lucide-react";

const CATEGORY_LABELS: Record<string, string> = {
  block_social_media: "Social Media",
  block_adult_content: "Adult Content",
  block_news: "News",
  block_gaming: "Gaming",
  block_streaming: "Streaming",
};

function policyCategories(policy: Record<string, unknown>): string[] {
  return Object.keys(CATEGORY_LABELS).filter((key) => policy[key] === true).map((key) => CATEGORY_LABELS[key]);
}

export default function Calendar() {
  const { data: organization } = useUserOrganization();
  const { data: policies = [] } = useOrganizationPolicies();
  const calendarEvents = useCalendarEvents(policies);

  const alwaysOnPolicies = policies.filter(
    (p) => p.schedule_type === 'always_on' && p.is_active
  );

  const handleEventClick = (event: unknown) => {
    console.log('Event clicked:', event);
  };

  const handleSlotClick = (slotInfo: { start: Date; end: Date }) => {
    console.log('Calendar slot clicked:', slotInfo);
  };

  useEffect(() => {
    console.log('📅 Calendar data:', {
      organization,
      policies,
      calendarEvents: calendarEvents.length,
      alwaysOnPolicies: alwaysOnPolicies.length,
    });
  }, [organization, policies, calendarEvents, alwaysOnPolicies]);

  if (!organization) {
    return (
      <SidebarProvider
        style={
          {
            "--sidebar-width": "16rem",
            "--header-height": "3rem",
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="inset" />
        <SidebarInset>
          <SiteHeader />
          <div className="flex flex-1 flex-col">
            <div className="@container/main flex flex-1 flex-col gap-6 p-6">
              <div className="flex items-center justify-center h-full">
                <div className="text-foreground text-lg">
                  Please set up your organization first
                </div>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "16rem",
          "--header-height": "3rem",
        } as React.CSSProperties
      }
    >
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader />
        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-6 p-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Policy Schedule</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Time-based policies appear on the calendar. Always-on policies are listed below.
              </p>
            </div>

            {alwaysOnPolicies.length > 0 && (
              <Card className="border-green-500/20 bg-green-500/5">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
                    <Shield className="size-4" />
                    Always-On Policies
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {alwaysOnPolicies.length} active
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-3">
                    These policies are enforced 24/7 and do not appear on the calendar.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {alwaysOnPolicies.map((policy) => {
                      const cats = policyCategories(policy as Record<string, unknown>);
                      const hasCustomDomains =
                        Array.isArray((policy as Record<string, unknown>).policy_domains) &&
                        ((policy as Record<string, unknown>).policy_domains as unknown[]).length > 0;
                      return (
                        <div
                          key={policy.id}
                          className="flex flex-col gap-1 rounded-lg border border-green-500/20 bg-background px-3 py-2 min-w-[160px]"
                        >
                          <div className="flex items-center gap-1.5 font-medium text-sm">
                            <Clock className="size-3 text-green-500" />
                            {policy.name}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {cats.map((cat) => (
                              <Badge key={cat} variant="outline" className="text-[10px] px-1.5 py-0">
                                <Ban className="size-2.5 mr-1" />
                                {cat}
                              </Badge>
                            ))}
                            {hasCustomDomains && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                <BookOpen className="size-2.5 mr-1" />
                                Custom domains
                              </Badge>
                            )}
                            {cats.length === 0 && !hasCustomDomains && (
                              <span className="text-[10px] text-muted-foreground">No categories set</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            <BigCalendarComponent
              events={calendarEvents}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
            />

            {calendarEvents.length === 0 && alwaysOnPolicies.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                <Shield className="size-8 opacity-30" />
                <p>No policies configured yet.</p>
                <p className="text-xs">Create a policy in Settings to get started.</p>
              </div>
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
